import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Flame, Facebook, Instagram, Linkedin, Twitter, Send, Download } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { COMPANY } from '../mock';
import api from '../lib/api';
import { mediaUrl } from '../lib/api';
import { useSite } from '../context/SiteContext';

export default function Footer() {
  const [email, setEmail] = useState('');
  const { toast } = useToast();
  const { settings } = useSite();

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post('/submissions', { type: 'newsletter', email });
      toast({ title: 'Subscribed!', description: 'You have been successfully subscribed to Closefire updates.' });
      setEmail('');
    } catch (err) {
      toast({ title: 'Subscription failed', description: 'Please try again in a moment.', variant: 'destructive' });
    }
  };

  return (
    <footer className="relative bg-[#05070d] text-gray-400 overflow-hidden">
      <div className="orb w-[420px] h-[420px] bg-primary/10 -top-40 -right-20" />
      <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-8">
        <div className="grid md:grid-cols-4 gap-10">
          <div>
            <Link to="/" className="flex items-center gap-2.5 mb-5">
              <img src={mediaUrl(settings?.logoUrl) || '/closefire-logo.png'} alt="Closefire" className="h-16 w-16 object-contain" />
              <span className="font-display text-3xl font-extrabold tracking-wide text-white">CLOSEFIRE</span>
            </Link>
            <p className="text-sm leading-relaxed">Comprehensive fire safety solutions — manufacturing, installation and maintenance to safeguard life and property.</p>
            <div className="flex items-center gap-3 mt-5">
              {[Facebook, Instagram, Linkedin, Twitter].map((Icon, i) => (
                <a key={i} href="#" className="grid place-items-center w-9 h-9 rounded-full bg-white/5 hover:bg-primary hover:text-white transition-colors"><Icon className="w-4 h-4" /></a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading font-bold text-white mb-5">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              {[['Home','/'],['About','/about'],['Products','/products'],['Projects','/projects'],['Partners','/partners'],['Careers','/careers'],['Contact','/contact']].map(([l,to]) => (
                <li key={to}><Link to={to} className="hover:text-primary transition-colors">{l}</Link></li>
              ))}
              <li><a href={settings?.employeeMailUrl || 'https://mail.google.com'} target="_blank" rel="noopener noreferrer" data-testid="footer-employee-login-link" className="hover:text-primary transition-colors">Employee Login</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-bold text-white mb-5">Get In Touch</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-3"><MapPin className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span>{COMPANY.address}</span></li>
              {COMPANY.phones.map((p) => <li key={p} className="flex gap-3"><Phone className="w-4 h-4 text-primary shrink-0" /><a href={`tel:${p}`} className="hover:text-primary">{p}</a></li>)}
              {COMPANY.emails.map((m) => <li key={m} className="flex gap-3"><Mail className="w-4 h-4 text-primary shrink-0" /><a href={`mailto:${m}`} className="hover:text-primary break-all">{m}</a></li>)}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-bold text-white mb-5">Stay Connected</h4>
            <p className="text-sm mb-4">Subscribe for fire-safety tips and product updates.</p>
            <form onSubmit={subscribe} className="flex gap-2">
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Your email" className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
              <Button type="submit" size="icon" className="shrink-0"><Send className="w-4 h-4" /></Button>
            </form>
            <a href={COMPANY.brochureUrl} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm font-heading font-semibold text-white hover:text-primary transition-colors">
              <Download className="w-4 h-4" /> Download Brochure
            </a>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm">
          <p>Copyright © 2026 Closefire. All rights reserved.</p>
          <div className="flex gap-6"><a href="#" className="hover:text-primary">Privacy Policy</a><a href="#" className="hover:text-primary">Cookies Policy</a></div>
        </div>
        <div className="pt-4 text-center md:text-right text-xs text-gray-500" data-testid="footer-credit">
          From Vision to Reality — Design and Orchestration by <span className="text-gray-300 font-heading font-semibold">DesignO'SDad</span>
        </div>
      </div>
    </footer>
  );
}
